import { z } from 'zod';
/**
 * Clientes del tenant + cartera (fiado). El registro de clientes vive en la
 * tabla `customers`; la deuda se deriva de los pagos `credit` y los abonos
 * (`customer_payments`). Ver `computeCustomerLedger`.
 */
export const customerIdentificationTypeSchema = z.enum(['CC', 'CE', 'NIT', 'PP', 'TI', 'PEP']);
/** Deja solo dígitos (y un `+` inicial) para buscar/deduplicar por teléfono. */
export const normalizePhone = (raw) => {
    const trimmed = raw.trim();
    const digits = trimmed.replace(/\D/g, '');
    return trimmed.startsWith('+') && digits.length > 0 ? `+${digits}` : digits;
};
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v === undefined || v === null || v === '' ? null : v));
export const customerCreateSchema = z.object({
    fullName: z.string().trim().min(1, 'El nombre es obligatorio').max(160),
    phone: optionalText(30).transform((v) => (v === null ? null : normalizePhone(v) || null)),
    identificationType: customerIdentificationTypeSchema.nullish().transform((v) => v ?? null),
    identificationNumber: optionalText(30),
    email: z
        .string()
        .trim()
        .max(320)
        .nullish()
        .transform((v) => (v === undefined || v === null || v === '' ? null : v))
        .pipe(z.string().email('Email inválido').nullable()),
});
/**
 * Aplica los abonos FIFO sobre los fiados (más antiguo primero) y devuelve
 * el saldo y los fiados que aún tienen pendiente. El caller debe excluir
 * pagos de órdenes canceladas y filas soft-deleted. Los campos extra de
 * cada crédito (ej. datos de la orden) se conservan en `pending`.
 */
export const computeCustomerLedger = (credits, collections) => {
    const creditedCents = credits.reduce((s, c) => s + c.amountCents, 0);
    const collectedCents = collections.reduce((s, c) => s + c.amountCents, 0);
    const sorted = [...credits].sort((a, b) => a.receivedAt - b.receivedAt || a.orderId.localeCompare(b.orderId));
    let toApply = collectedCents;
    const pending = [];
    for (const credit of sorted) {
        const applied = Math.min(credit.amountCents, toApply);
        toApply -= applied;
        const pendingCents = credit.amountCents - applied;
        if (pendingCents > 0)
            pending.push({ ...credit, pendingCents });
    }
    return {
        creditedCents,
        collectedCents,
        balanceCents: Math.max(0, creditedCents - collectedCents),
        pending,
    };
};
//# sourceMappingURL=customer.js.map